import { useEffect, useState, useCallback, useMemo } from 'react';

interface CountdownTimerProps {
    startDate: string;
    serverTime: number;
    className?: string;
    processHours?: number;
}

export const CountdownTimer = ({ startDate, serverTime, className, processHours = 24 }: CountdownTimerProps) => {
    const [timeLeft, setTimeLeft] = useState<string>('');
    
    // Memoize serverTimeDiff since it only needs to be calculated once
    // Only calculate if serverTime is valid (greater than 0)
    const serverTimeDiff = useMemo(() => {
        if (serverTime <= 0) {
            return 0;
        }
        return serverTime - Date.now();
    }, [serverTime]);

    // Memoize calculateTimeLeft to prevent recreation on every render
    const calculateTimeLeft = useCallback(() => {
        // Don't calculate if serverTime is not ready
        if (serverTime <= 0) {
            return '--:--:--';
        }

        // Parse startDate as UTC - ISO 8601 strings with timezone are parsed correctly by Date
        // Date.parse handles ISO 8601 format (including timezone) properly
        const startTime = Date.parse(startDate);
        
        // Validate that startDate was parsed correctly
        if (isNaN(startTime)) {
            return '--:--:--';
        }

        const now = Date.now() + serverTimeDiff;
        const processingTime = processHours * 60 * 60 * 1000;
        const endTime = startTime + processingTime;
        const difference = endTime - now;

        if (difference <= 0) {
            return '00:00:00';
        }

        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);

        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }, [startDate, serverTimeDiff, processHours, serverTime]);

    useEffect(() => {
        // Don't set up timer if serverTime is not ready
        if (serverTime <= 0) {
            setTimeLeft('--:--:--');
            return;
        }

        // Initial calculation
        setTimeLeft(calculateTimeLeft());
        
        // Set up interval
        const timer = setInterval(() => {
            const newTimeLeft = calculateTimeLeft();
            // Only update state if the time has actually changed
            setTimeLeft(prev => prev !== newTimeLeft ? newTimeLeft : prev);
        }, 1000);

        return () => clearInterval(timer);
    }, [calculateTimeLeft, serverTime]); // Added serverTime dependency

    return <div className={className}>{timeLeft}</div>;
};
